import gql from 'graphql-tag';

import {
  AlbumListingDetails,
  PhotoThumbnails,
  PhotoUrlTypeFields,
  AlbumDetailsForAssign,
  AlbumPhotoCount,
  AlbumForReview,
  AlbumForStatusItems,
  AlbumLocationFields,
  AlbumRootAlbumFolderBaseDetails,
} from './album';
import { AlbumPhotoGroupBasicDetails } from './album-photo-group';
import { AlbumCustomPrintDetails } from './custom-prints';
import { AutoContributorSettingsDetails } from './contributor-assignments';
import { AlbumPhotoFavoriteDetails, PhotoFavoriteDetails } from './photo';
import {
  printProductBaseDetails,
  printProductSizeRoesGraphicNodeDetails,
  printProductSizeServiceDetails,
} from './print-product';
import { printPartnerOrderServiceDetails } from './print-partner';
import { albumCustomPrintDetails } from './album-custom-print';
import {
  AccountAddressFields,
  UserDetailsWithImages,
  VendorDetails,
} from './account';
import { AlbumAlbumFolderTagDetails } from './album-folder-tags';
import { DigitalBundleDetails } from './digital-bundles';
import { AlbumFolderBroadcastSequenceDetails } from './broadcasts';
import { DiscountTemplateDetails } from './discount-templates';
import { HierarchyGradeDetails } from './hierarchy-grades';

export const AlbumFolderBlackoutConfig = gql`
  fragment AlbumFolderBlackoutConfig on AlbumFolder {
    id
    album_blackout_config {
      timezone
      hours_start
      hours_end
    }
  }
`;

export const WebGalleryAlbumFolderFields = gql`
  fragment WebGalleryAlbumFolderFields on AlbumFolder {
    is_web_gallery
    is_private_web_gallery
    download_without_watermark
    download_without_purchase
    print_without_watermark
    social_share_enabled
    hash_code
    web_gallery_print_purchases_enabled
    file_comment_privacy
    invite_only_access
    web_gallery_price_per_photo {
      value
    }
  }
`;

export const AlbumFolderCharityDetails = gql`
  fragment AlbumFolderCharityDetails on AlbumFolder {
    has_charity
    charity_name
    charity_percentage
    charity_description
    charity_footnote
  }
`;

export const AlbumFolderLimitedTimeOfferFields = gql`
  fragment AlbumFolderLimitedTimeOfferFields on AlbumFolder {
    limited_time_offer_price {
      value
      currency
    }
    limited_time_offer_active
    limited_time_offer_date_start
    limited_time_offer_date_end
  }
`;

export const AlbumFolderPhotoFinderDetails = gql`
  fragment AlbumFolderPhotoFinderDetails on AlbumFolder {
    id
    photo_finder {
      credits_balance {
        total
      }
      photos_count
      photo_finder_photos_count_pending_charge: photos_count(
        pending_charge_only: true
      )
    }
  }
`;

export const AlbumFolderContributeReminderDetails = gql`
  fragment AlbumFolderContributeReminderDetails on AlbumFolder {
    contribute_reminders {
      days_offset
      reminder_date
      is_pending
    }
  }
`;

export const AlbumFolderBaseDetails = gql`
  fragment AlbumFolderBaseDetails on AlbumFolder {
    id
    purchase_type
    name
    join_code
    album_type
    album_subtype
    price {
      currency
      value
    }
    is_demo
    upload_mode
    has_free_trial
    join_type
    join_without_purchase
    force_join_without_purchase
    requires_album_folder_onboarding
    individual_albums
    display_photos_attribution
    day_pricings_enabled
    tag_join_enabled
    requires_tag_onboarding
    root {
      id
      created_at
    }
    parent {
      id
      name
    }
    start_date
    end_date
    discovery_selection_info
    joins_enabled
    search_subscription_credits_joins_enabled
    has_picture_day_enabled_album
    org_pay_photo_pricing_enabled
    picture_day_enabled
    photo_sharing_mode
    album_price_mode
    video_guestbook_enabled
    file_comment_privacy
    privacy
    print_partner {
      id
    }
    purchased_event {
      id
    }
    storage {
      max
      used
    }
    join_requires_purchase
    prepaid_digital_bundles_enabled
    prepaid_print_packages_enabled
    prepaid_photo_pricings_enabled
    prepaid_custom_prints_enabled
    green_screen_enabled
    individual_photos_upload_type
    team_photos_upload_type
    ...AlbumFolderCharityDetails
    ...WebGalleryAlbumFolderFields
    ...AlbumFolderLimitedTimeOfferFields
  }
  ${AlbumFolderCharityDetails}
  ${WebGalleryAlbumFolderFields}
  ${AlbumFolderLimitedTimeOfferFields}
`;

export const AlbumFolderDetails = gql`
  fragment AlbumFolderDetails on AlbumFolder {
    id
    account {
      ...UserDetailsWithImages
    }
    purchase_type
    name
    join_code
    join_type
    album_type
    album_subtype
    shoot_type
    skip_subscription_setup_prompt
    revenue_share_percentage
    day_pricings_discount_percentage
    siblings_discount_percentage
    price {
      currency
      value
    }
    is_demo
    upload_mode
    has_free_trial
    cover_photos(slice: { limit: 1 }) {
      records {
        id
        faces_box
        thumbnail_urls(ignore_watermark: true) {
          ...PhotoUrlTypeFields
        }
      }
    }
    individual_albums
    force_join_without_purchase
    requires_album_folder_onboarding
    requires_tag_onboarding
    root {
      id
      created_at
      upload_mode
      photo_sharing_mode
      green_screen_enabled
      uniform_tags: tags(
        slice: { limit: 1 }
        album_folder_tag_types: [UNIFORM]
      ) {
        total
        records {
          id
          ... on UniformAlbumFolderTag {
            uniform_enabled
            uniform_numbered
          }
        }
      }
    }
    parent {
      id
      name
    }
    join_without_purchase
    discovery_selection_info
    auto_contributor_share_url
    auto_contributor_settings {
      ...AutoContributorSettingsDetails
    }
    consumer_photos_sort_by
    consumer_photos_sort_direction
    ...WebGalleryAlbumFolderFields
    ...AlbumFolderCharityDetails
    ...AlbumFolderLimitedTimeOfferFields
    location {
      ...AlbumLocationFields
    }
    joins_enabled
    search_subscription_credits_joins_enabled
    has_picture_day_enabled_album
    picture_day_enabled
    is_web_gallery
    digital_downloads_pricing {
      type
      print_package_mode
      print_package_upsell_price {
        value
        currency
      }
      is_free
      price {
        value
        currency
      }
    }
    org_pay_photo_pricing_enabled
    photo_sharing_mode
    privacy
    created_at
    public_join_code_verification_enabled
    album_price_mode
    hash_code
    file_comment_privacy
    print_partner {
      id
    }
    green_screen_enabled
    individual_photos_upload_type
    team_photos_upload_type
    display_order
    multi_pose_print_packages_enabled
    multi_identity_print_packages_enabled
    multi_identity_digital_bundles_enabled
    matching_tags_enabled
    owner_purchased_joins_enabled
    hold_matched_photo_deliveries
    ...AlbumFolderContributeReminderDetails
  }
  ${PhotoUrlTypeFields}
  ${WebGalleryAlbumFolderFields}
  ${AlbumFolderCharityDetails}
  ${AlbumFolderLimitedTimeOfferFields}
  ${AutoContributorSettingsDetails}
  ${AlbumLocationFields}
  ${UserDetailsWithImages}
  ${AlbumFolderContributeReminderDetails}
`;

export const AlbumFolderCreatorAccessDetails = gql`
  fragment AlbumFolderCreatorAccessDetails on AlbumFolder {
    id
    password
    has_password
    invite_only_access
  }
`;

export const AlbumFolderForReviewDetails = gql`
  fragment AlbumFolderForReviewDetails on AlbumFolder {
    id
    photos_for_review: album_photos(album_photo_filter: REVIEWING) {
      total
    }
  }
`;

export const AlbumFolderPendingCustomPrintDetails = gql`
  fragment AlbumFolderPendingCustomPrintDetails on AlbumFolder {
    id
    pending_purchased_custom_prints: purchased_custom_prints(
      slice: { limit: 1 }
      order_statuses: [PENDING]
    ) {
      total
    }
  }
`;

export const AlbumFolderEventSettingsBasicFields = gql`
  fragment AlbumFolderEventSettingsBasicFields on AlbumFolderEventSettings {
    bride_name
    groom_name
    first_name
    last_name
    charity_name
  }
`;

export const AlbumFolderEventSettingsFields = gql`
  fragment AlbumFolderEventSettingsFields on AlbumFolderEventSettings {
    bride_name
    groom_name
    couple_email_address
    relationship
    anniversary_type
    first_name
    last_name
    charity_name
    event_date
    date_of_birth
    date_of_passing
    created_at
    updated_at
    wedding_year
    work_start_year
    graduation_date
  }
`;

export const AlbumFolderMainCoverPhoto = gql`
  fragment AlbumFolderMainCoverPhoto on AlbumFolder {
    id
    main_cover_photo: custom_cover_photos(slice: { limit: 1 }) {
      records {
        id
        display_order
        photo {
          id
          thumbnail_urls {
            ...PhotoUrlTypeFields
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const AlbumFolderAlbumChildren = gql`
  fragment AlbumFolderAlbumChildren on AlbumFolder {
    id
    children {
      albums {
        total
        records {
          ...AlbumListingDetails
          ...AlbumForReview
          ...AlbumForStatusItems
          album_photo_groups(slice: { limit: 999 }) {
            total
            records {
              ...AlbumPhotoGroupBasicDetails
            }
          }
        }
      }
    }
  }
  ${AlbumListingDetails}
  ${AlbumPhotoGroupBasicDetails}
  ${AlbumForReview}
  ${AlbumForStatusItems}
`;

export const AlbumFolderChildrenCount = gql`
  fragment AlbumFolderChildrenCount on AlbumFolder {
    id
    children {
      album_folders {
        total
      }
      albums {
        total
        records {
          ...AlbumPhotoCount
        }
      }
    }
  }
  ${AlbumPhotoCount}
`;

export const AlbumFolderChildrenDetails = gql`
  fragment AlbumFolderChildrenDetails on AlbumFolder {
    id
    children {
      album_folders {
        total
        records {
          ...AlbumFolderDetails
          ...AlbumFolderChildrenCount
        }
      }
      albums {
        total
        records {
          ...AlbumListingDetails
          ...AlbumForReview
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderChildrenCount}
  ${AlbumListingDetails}
  ${AlbumForReview}
`;

export const AlbumFolderPROChildrenDetails = gql`
  fragment AlbumFolderPROChildrenDetails on AlbumFolder {
    id
    children {
      album_folders {
        total
        records {
          ...AlbumFolderDetails
          ...AlbumFolderChildrenCount
          ...AlbumFolderForReviewDetails
          has_album
          album_photos {
            total
          }
          album_photos_count
          individual_photos_upload_type
          team_photos_upload_type
        }
      }
      albums {
        total
        records {
          ...AlbumListingDetails
          ...AlbumForReview
          album_folder_tags(slice: { limit: 999 }) {
            total
            records {
              ...AlbumAlbumFolderTagDetails
            }
          }
        }
      }
    }
  }
  ${AlbumFolderDetails}
  ${AlbumFolderChildrenCount}
  ${AlbumListingDetails}
  ${AlbumForReview}
  ${AlbumFolderForReviewDetails}
  ${AlbumAlbumFolderTagDetails}
`;

export const AlbumFolderWatermarkConfig = gql`
  fragment AlbumFolderWatermarkConfig on AlbumFolder {
    watermark_config {
      id
      placement
      logo_image {
        ...PhotoThumbnails
      }
      asset_image {
        id
        url
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumFolderHeroImageConfig = gql`
  fragment AlbumFolderHeroImageConfig on AlbumFolder {
    hero_image {
      id
      url
    }
  }
`;

export const AlbumFolderWithChildrenData = gql`
  fragment AlbumFolderWithChildrenData on AlbumFolder {
    id
    name
    individual_albums
    processed_photos: photos(processed_photos: true) {
      total
    }
    children {
      albums {
        records {
          ...AlbumDetailsForAssign
        }
      }
    }
  }
  ${AlbumDetailsForAssign}
`;

export const AlbumFolderPhotos = gql`
  fragment AlbumFolderPhotos on AlbumFolder {
    id
    photos: photos(processed_photos: true, slice: { limit: 25, offset: 0 }) {
      total
      count
      records {
        id
        status
        created_at
        photo {
          ...PhotoThumbnails
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumFolderPhotosDetails = gql`
  fragment AlbumFolderPhotosDetails on AlbumFolder {
    id
    unprocessed_photos: photos(processed_photos: false) {
      total
    }
    processed_photos: photos(processed_photos: true) {
      total
    }
    album_photos {
      total
    }
    album_photos_count
  }
`;

export const AlbumFolderAlbumCustomPrintForPurchaseDetails = gql`
  fragment AlbumFolderAlbumCustomPrintForPurchaseDetails on AlbumFolder {
    id
    album_custom_prints(
      custom_print_types: [CUSTOM_PRINT, COLLAGE]
      slice: { limit: 1 }
    ) {
      total
      records {
        ...AlbumCustomPrintDetails
      }
    }
  }
  ${AlbumCustomPrintDetails}
`;

export const AlbumFolderPrintOrderConfiguration = gql`
  fragment AlbumFolderPrintOrderConfiguration on AlbumFolder {
    print_order_configuration {
      id
      print_partner_order_services(slice: { limit: 999 }) {
        total
        records {
          id
          print_partner_order_service {
            ...printPartnerOrderServiceDetails
          }
        }
      }
    }
  }
  ${printPartnerOrderServiceDetails}
`;

export const AlbumFolderPrintAssets = gql`
  fragment AlbumFolderPrintAssets on AlbumFolder {
    print_assets(slice: { limit: 999 }) {
      total
      records {
        id
        print_asset {
          id
          name
          type
          jersey_colors(slice: { limit: 999 }) {
            records {
              id
              jersey_color
            }
          }
          asset_image {
            id
            url
            width
            height
          }
        }
      }
    }
  }
`;

export const AlbumFolderPricingDetails = gql`
  fragment AlbumFolderPricingDetails on AlbumFolder {
    id
    price {
      value
      currency
    }
    is_demo
    org_pay_photo_pricing_enabled
    search_subscription_credits_joins_enabled
    album_folder_print_packages(slice: { limit: 9999 }) {
      total
      records {
        id
        print_package {
          id
          preview_asset_image {
            id
            url
          }
          account {
            ...VendorDetails
          }
          pricing_categories {
            records {
              id
              pricing_category {
                id
              }
            }
          }
          print_assets {
            records {
              id
              print_asset {
                id
              }
            }
          }
          print_order_configuration {
            print_partner_order_services {
              total
              records {
                id
                print_partner_order_service {
                  ...printPartnerOrderServiceDetails
                }
              }
            }
            total_price {
              value
              currency
            }
          }
        }
        items {
          records {
            id
            price {
              value
            }

            discount_price {
              value
            }
            print_package_item {
              id
              type {
                id
                name
              }
              print_product_configuration {
                print_product {
                  id
                }
                print_product_size {
                  id
                  services {
                    total
                    records {
                      ...printProductSizeServiceDetails
                    }
                  }
                  roes_info {
                    graphic_nodes {
                      total
                      records {
                        ...printProductSizeRoesGraphicNodeDetails
                      }
                    }
                  }
                }
                print_product_size_services {
                  total
                  records {
                    id
                    print_product_size_service {
                      ...printProductSizeServiceDetails
                    }
                  }
                }
                print_product_size_roes_graphic_node {
                  ...printProductSizeRoesGraphicNodeDetails
                }
              }
              custom_print {
                id
                name
              }
              print_package {
                id
              }
              album_folder_print_package_items(slice: { limit: 1 }) {
                total
                records {
                  id
                }
              }
            }
            album_folder_tag {
              ... on IRecord {
                id
              }
              ... on IAlbumFolderTag {
                name
              }
            }
          }
        }
      }
    }
    custom_prints(slice: { limit: 9999 }) {
      records {
        id
      }
    }
    album_folder_custom_prints(slice: { limit: 9999 }) {
      records {
        id
        custom_print {
          id
        }
      }
    }
    photo_pricing {
      id
      name
      pricing_categories {
        records {
          id
          pricing_category {
            id
          }
        }
      }
      print_assets {
        records {
          id
          print_asset {
            id
          }
        }
      }
    }
    digital_bundles {
      total
      records {
        ...DigitalBundleDetails
      }
    }
    album_price_mode
    join_without_purchase
    join_requires_purchase
    prepaid_digital_bundles_enabled
    prepaid_print_packages_enabled
    prepaid_photo_pricings_enabled
    prepaid_custom_prints_enabled
    pricing_categories {
      total
      records {
        id
        pricing_category {
          id
          name
        }
      }
    }
    siblings_discount_percentage
    ...AlbumFolderPrintOrderConfiguration
    ...AlbumFolderPrintAssets
  }
  ${printProductSizeServiceDetails}
  ${printPartnerOrderServiceDetails}
  ${printProductSizeRoesGraphicNodeDetails}
  ${VendorDetails}
  ${DigitalBundleDetails}
  ${AlbumFolderPrintOrderConfiguration}
  ${AlbumFolderPrintAssets}
`;

export const AlbumFolderShutterbugDetails = gql`
  fragment AlbumFolderShutterbugDetails on AlbumFolder {
    id
    name
    description
    event_type
    event_venue
    start_date
    end_date
    privacy
    is_private_web_gallery
    password
    invite_only_access
    auto_contributor_settings {
      ...AutoContributorSettingsDetails
    }
    location {
      ...AlbumLocationFields
    }
  }
  ${AutoContributorSettingsDetails}
  ${AlbumLocationFields}
`;

export const AlbumPhotoFaceHighlightsForAlbumFolder = gql`
  fragment AlbumPhotoFaceHighlightsForAlbumFolder on AlbumPhoto {
    id
    photo {
      id
      ...PhotoThumbnails
      ...PhotoFavoriteDetails
      share_blocked
      is_owner
    }
    face_highlights {
      records {
        id
        rating
        type
        face {
          id
          facebox
          matched_photos(slice: { limit: 1 }) {
            records {
              id
              identity {
                id
                name
                current_photo {
                  ...PhotoThumbnails
                }
              }
            }
          }
        }
        album {
          id
          root_album_folder {
            id
          }
        }
      }
    }
    ...AlbumPhotoFavoriteDetails
    album {
      id
      name
      hash_code
      ...AlbumRootAlbumFolderBaseDetails
    }
    album_highlight {
      id
    }
  }
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
  ${AlbumPhotoFavoriteDetails}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const albumFolderPhotoPricingDetails = gql`
  fragment albumFolderPhotoPricingDetails on AlbumFolder {
    id
    photo_pricing {
      id
      account {
        ...VendorDetails
      }
      digital_downloads_price {
        value
        currency
      }
      requires_package_purchase
      print_product_items(slice: { limit: 99 }) {
        total
        records {
          id
          print_product {
            ...printProductBaseDetails
          }
          print_product_size {
            id
            print_size {
              id
              name
            }
          }
          print_product_size_mount {
            id
          }
          name
          price {
            currency
            value
          }
        }
      }
      print_items(slice: { limit: 99 }) {
        total
        records {
          id
          price {
            value
            currency
          }
          type {
            id
            name
          }
        }
      }
    }
  }
  ${printProductBaseDetails}
  ${VendorDetails}
`;

export const albumFolderPrintPackageDetails = gql`
  fragment albumFolderPrintPackageDetails on AlbumFolder {
    id
    album_folder_print_packages {
      total
      records {
        id
        album_folder {
          id
        }
        print_package {
          id
          name
          internal_name
          max_checkout_items
          preview_asset_image {
            id
            url
          }
          account {
            ...VendorDetails
          }
          price(album_folder_id: $album_folder_id) {
            value
            currency
          }
          discount_price {
            value
            currency
          }
          digital_downloads_mode
          digital_downloads_upsell_price {
            value
            currency
          }
          print_package_bundle_discount_price {
            value
            currency
          }
          print_assets(slice: { limit: 999 }) {
            total
            records {
              id
              print_asset {
                id
                name
                type
                jersey_colors(slice: { limit: 999 }) {
                  records {
                    id
                    jersey_color
                  }
                }
                asset_image {
                  id
                  url
                  width
                  height
                }
              }
            }
          }
          items(slice: { limit: 99 }) {
            total
            records {
              id
              quantity
              type {
                id
                name
              }
              album_folder_tag {
                ... on CustomAlbumFolderTag {
                  id
                  name
                }
              }
              price {
                value
                currency
              }
              discount_price {
                value
                currency
              }
              is_optional
              custom_print {
                id
                name
                type
                subtype
                preview_image {
                  id
                  url
                }
                photos_no
                background_image {
                  id
                  url
                }
                account {
                  id
                  logo_image {
                    id
                    url
                  }
                }
                price {
                  value
                  currency
                }
                custom_print_caption_enabled
                shipping_required
                shipping_enabled
                card {
                  id
                  card_side_groups {
                    records {
                      id
                      side
                      card_sides {
                        records {
                          id
                          card_template {
                            id
                            orientation
                            print_product {
                              id
                              print_size {
                                id
                              }
                              print_partner {
                                id
                                name
                              }
                              sizes(slice: { limit: 999 }) {
                                records {
                                  id
                                  sku
                                  print_size {
                                    id
                                    name
                                  }
                                }
                              }
                            }
                            print_size {
                              id
                              name
                              width
                              height
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              print_product_configuration {
                print_product {
                  id
                  name
                  is_standard
                  card {
                    id
                  }
                }
                print_product_size {
                  id
                  print_size {
                    id
                    name
                  }
                  total_price {
                    value
                    currency
                  }
                  roes_info {
                    image_nodes {
                      id
                      ui_description
                      ui_label
                      bounds
                      is_required
                    }
                    text_nodes {
                      id
                      ui_description
                      ui_label
                      bounds
                      is_required
                      default_text
                    }
                  }
                }
                print_product_size_services {
                  total
                  records {
                    id
                    print_product_size_service {
                      ...printProductSizeServiceDetails
                    }
                  }
                }
                print_product_size_roes_graphic_node {
                  ...printProductSizeRoesGraphicNodeDetails
                }
              }
              album_folder_print_package_items(slice: { limit: 99 }) {
                total
                records {
                  id
                  album_folder_print_package {
                    album_folder {
                      id
                    }
                  }
                  price {
                    value
                    currency
                  }
                  discount_price {
                    value
                    currency
                  }
                  album_folder_tag {
                    ... on CustomAlbumFolderTag {
                      id
                      name
                    }
                  }
                }
              }
              digital_bundle {
                ...DigitalBundleDetails
              }
              grants_purchased_identity
            }
          }
        }
      }
    }
  }
  ${VendorDetails}
  ${printProductSizeServiceDetails}
  ${printProductSizeRoesGraphicNodeDetails}
  ${DigitalBundleDetails}
`;

export const AlbumFolderPurchaseDetails = gql`
  fragment AlbumFolderPurchaseDetails on AlbumFolder {
    id
    picture_day_enabled
    multi_pose_print_packages_enabled
    multi_identity_print_packages_enabled
    multi_identity_digital_bundles_enabled
    ...albumFolderPrintPackageDetails
    ...albumFolderPhotoPricingDetails
    album_custom_prints(
      slice: { limit: 99 }
      unique_custom_prints: true
      custom_print_types: [CUSTOM_PRINT]
      custom_print_subtype: null
    ) {
      total
      records {
        ...albumCustomPrintDetails
      }
    }
    digital_bundles {
      total
      records {
        ...DigitalBundleDetails
      }
    }
    collage_custom_prints: album_custom_prints(
      slice: { limit: 99 }
      unique_custom_prints: true
      custom_print_types: [PRINT_PRODUCT]
      custom_print_subtype: COLLAGE
    ) {
      total
      records {
        ...albumCustomPrintDetails
      }
    }
    account_card_album_custom_prints: album_custom_prints(
      slice: { limit: 99 }
      unique_custom_prints: true
      custom_print_types: [PRINT_PRODUCT]
      custom_print_subtype: null
    ) {
      total
      records {
        ...albumCustomPrintDetails
      }
    }
    specialty_custom_prints: album_custom_prints(
      slice: { limit: 99 }
      unique_custom_prints: true
      custom_print_types: [CUSTOM_PRINT]
      custom_print_subtype: SPECIALTY_PRINT
    ) {
      total
      records {
        ...albumCustomPrintDetails
      }
    }
    ...AlbumFolderPrintOrderConfiguration
    ...AlbumFolderPrintAssets
  }
  ${albumFolderPrintPackageDetails}
  ${albumFolderPhotoPricingDetails}
  ${albumCustomPrintDetails}
  ${DigitalBundleDetails}
  ${AlbumFolderPrintOrderConfiguration}
  ${AlbumFolderPrintAssets}
`;

export const AlbumFolderPurchasedEventAddonDetails = gql`
  fragment AlbumFolderPurchasedEventAddonDetails on AlbumFolder {
    purchased_event_addon_types
    purchased_event_addons {
      total
      records {
        id
        event_addon_type
        album {
          id
          name
        }
      }
    }
  }
`;

export const AlbumFolderTaxDetails = gql`
  fragment AlbumFolderTaxDetails on AlbumFolder {
    taxes {
      id
      tax_type
      jurisdiction
      state {
        id
        name
        abbreviation
      }
      tax_percentage
      tax_product_category
    }
  }
`;

export const AlbumFolderBroadcastSequencesDetails = gql`
  fragment AlbumFolderBroadcastSequencesDetails on AlbumFolder {
    broadcast_sequences(slice: { limit: 999 }) {
      total
      records {
        ...AlbumFolderBroadcastSequenceDetails
      }
    }
  }
  ${AlbumFolderBroadcastSequenceDetails}
`;

export const AlbumFolderDiscountTemplateDetails = gql`
  fragment AlbumFolderDiscountTemplateDetails on AlbumFolder {
    discount_template {
      ...DiscountTemplateDetails
    }
  }
  ${DiscountTemplateDetails}
`;

export const AlbumFolderHierarchyGradeDetails = gql`
  fragment AlbumFolderHierarchyGradeDetails on AlbumFolder {
    grades(slice: { limit: 99 }) {
      total
      records {
        ...HierarchyGradeDetails
      }
    }
  }
  ${HierarchyGradeDetails}
`;

export const AlbumFolderYearbookSettings = gql`
  fragment AlbumFolderYearbookSettings on AlbumFolder {
    yearbook_settings {
      consumer_selection_enabled
      thumbnail_settings {
        width
        height
        full_size_facebox
      }
    }
  }
`;

export const AlbumFolderHierarchyShippingSettings = gql`
  fragment AlbumFolderHierarchyShippingSettings on AlbumFolder {
    hierarchy_shipping_settings {
      account_address {
        ...AccountAddressFields
      }
      expires_at
      shipping_mode
    }
  }
  ${AccountAddressFields}
`;

export const AlbumFolderPrintAssetDetails = gql`
  fragment AlbumFolderPrintAssetDetails on AlbumFolder {
    id
    ...AlbumFolderPrintAssets
    album_folder_print_packages(slice: { limit: 999 }) {
      records {
        id
        print_package {
          id
          print_assets(slice: { limit: 999 }) {
            records {
              id
              print_asset {
                id
                name
                type
                jersey_colors(slice: { limit: 999 }) {
                  records {
                    id
                    jersey_color
                  }
                }
                asset_image {
                  id
                  url
                  width
                  height
                }
              }
            }
          }
        }
      }
    }
    photo_pricing {
      id
      print_assets(slice: { limit: 999 }) {
        records {
          id
          print_asset {
            id
            name
            type
            jersey_colors(slice: { limit: 999 }) {
              records {
                id
                jersey_color
              }
            }
            asset_image {
              id
              url
              width
              height
            }
          }
        }
      }
    }
  }
  ${AlbumFolderPrintAssets}
`;
